import { ObjectId } from '@freightview/object-id'
import clsx from 'clsx'
import type { PropsWithChildren } from 'react'

import { FvButton, type FvButtonProps } from '@fv/client-components'
import { submitForm, useFormModel } from '@fv/client-core'

import { InputGroup, InputGroupWrapper } from '@/components/inputs/InputGroup'

import { FreightClassEstimate } from '../FreightClassEstimate'
import { calculateFreightClassEstimate } from '../freight-class'
import { ContainsForm } from './ContainsForm'
import { ContainsRow } from './ContainsRow'
import {
  DeclaredValueField,
  DescriptionField,
  DimensionsField,
  FreightClassField,
  HandlingUnitTypeField,
  IsHazardousField,
  ItemStopsField,
  PackagingField,
  ProductCatalogField,
  QuantityField,
  SecondaryPackageTypeField,
  StackableField,
  WeightField,
} from './Fields'
import { HazmatForm } from './HazmatForm'
import { defaultLoadContainsItem } from './loadItemHelpers'
import { useItemActions, useItemsState } from './loadItemsContext'
import { NmfcField } from './NmfcField'
import type { FormLayoutProps, ItemChangeFn, LoadItemFormModel } from './types'
import { LoadItemSchema } from './useLoadHandlingUnitSchema'

export const FreightItemForm = (props: FormLayoutProps) => {
  const { activateContains, setEditingContains } = useItemActions()
  const { step, disabled, itemForm, workflow, stops, isTemplate } =
    useItemsState()
  const { register, onChange, value } = props
  const isLTL = workflow === 'ltl'
  const { register: simpleContainsRegister } = useFormModel({
    value: value.contains?.[0] ?? defaultLoadContainsItem(),
    schema: LoadItemSchema,
    onChange: v => {
      let contains: LoadItemFormModel[] | undefined = [v]
      if (!v.quantity && !v.type) {
        contains = undefined
      }
      onChange({ contains })
    },
  })
  const handleContainsOpen = () => {
    if (itemForm && !itemForm?.checkValidity()) {
      submitForm(itemForm)
      return
    } else {
      itemForm?.reset()
    }
    setEditingContains(value)
  }
  const handleContainsChange =
    (index: number): ItemChangeFn =>
    v => {
      onChange({
        ...value,
        contains: value.contains?.map((c, ix) =>
          ix === index ? { ...c, ...v } : c,
        ),
      })
    }
  const simpleContainsQuantity = simpleContainsRegister('quantity')
  const id = value._id ?? new ObjectId().toHexString()
  const isQuoting = step === 'quoting'
  const isBooking = step === 'booking'
  const isMultiClass = value.handlingUnitType === 'multi'
  const containsLength = value.contains?.length ?? 0

  return (
    <>
      <HandlingUnitTypeField
        register={register}
        onChange={onChange}
        value={value}
      />
      {stops && stops.length > 2 && (
        <ItemStopsField value={value} onChange={onChange} stops={stops} />
      )}
      <ProductCatalogField
        className={clsx({
          'basis-full': isQuoting,
          'basis-full @cb1450:basis-5/12': isBooking,
        })}
        required={isBooking && !isTemplate}
        doublesAsDescription={isBooking}
        onChange={onChange}
        value={value}
        disabled={disabled}
        label={isBooking ? 'Description' : 'Search'}
      />
      {isQuoting && (
        <DescriptionField
          onChange={onChange}
          value={value}
          register={register}
          hasNmfcSearch={!isMultiClass}
          className="basis-full"
        />
      )}
      <PackagingField
        className={clsx({
          'basis-3/5': !isMultiClass && isQuoting,
          'basis-1/3': isMultiClass && isQuoting,
          'b1450:basis-3/12 basis-2/12 flex-1': isBooking,
        })}
        register={register}
        value={value}
        onChange={onChange}
      />
      {isLTL && (
        <StackableField
          className={clsx({
            'basis-2/5': isQuoting && !isMultiClass,
            'basis-1/3': isQuoting && isMultiClass,
            'basis-2/12': isBooking,
          })}
          register={register}
        />
      )}
      {isBooking && !isMultiClass && (
        <>
          <InputGroup
            className="basis-1/12"
            inputProps={simpleContainsQuantity}
            inputType="amount"
            label="Contains"
            required={!isTemplate && simpleContainsQuantity.required}
            locked={value?.readOnly?.saidToContain}
          />
          <SecondaryPackageTypeField
            className={clsx({
              'basis-2/12': isLTL,
              'basis-4/12': !isLTL,
            })}
            register={simpleContainsRegister}
            parentPackageType={value.type}
            label="Units"
          />
        </>
      )}
      {!isMultiClass && (
        <>
          <QuantityField
            className={clsx({
              'basis-1/5': isQuoting,
              'basis-1/12': isBooking,
            })}
            register={register}
          />
          <WeightField
            className={clsx({
              'basis-2/5': isQuoting,
              'basis-2/12': isBooking,
            })}
            register={register}
            label="Total weight"
          />
        </>
      )}

      <DimensionsField
        register={register}
        className={clsx(
          `form-group flex mb-0 [&>div]:p-0 [&>div:not(:first-child)]:-ml-px`,
          {
            'basis-2/5': isQuoting && !isMultiClass,
            'basis-1/3': isMultiClass,
            'basis-3/12': isBooking,
          },
        )}
      />
      {!isMultiClass && (
        <>
          {isLTL && (
            <>
              <NmfcField
                register={register}
                className={clsx({
                  'flex-1 @cb1450:basis-2/12': isBooking,
                  'basis-1/3': isQuoting,
                })}
                onChange={onChange}
                value={value}
              />
              <FreightClassField
                className={clsx({
                  'basis-1/3': isQuoting,
                  'basis-2/12': isBooking,
                })}
                register={register}
                estimate={
                  isBooking && !value.fullNmfc
                    ? calculateFreightClassEstimate(value)
                    : null
                }
              />
            </>
          )}
          {!isLTL && (
            <DeclaredValueField
              register={register}
              className={clsx({
                'basis-4/12': isBooking,
              })}
            />
          )}
          <IsHazardousField
            type="radio"
            register={register}
            className={clsx({
              'basis-1/3': isQuoting,
              'basis-2/12': isBooking,
            })}
          />
        </>
      )}
      {value.isHazardous && isBooking && !isMultiClass && (
        <div className="form-row m-1 grid basis-full grid-cols-6 border border-fv-orange-bright bg-fv-orange-25  px-4 pb-2 pt-4">
          <HazmatForm
            value={value.hazardous}
            readOnly={value.readOnly}
            itemId={id}
            onChange={hazardous => onChange({ ...value, hazardous })}
            disabled={disabled}
          />
        </div>
      )}

      {isQuoting && !isMultiClass && (
        <div className="mb-1 basis-full px-1">
          <FreightClassEstimate
            {...value}
            densityCalculationOnly={!!value.fullNmfc}
          >
            <div className="border-fv-gray mx-1 mt-2 basis-full border-b " />
          </FreightClassEstimate>
        </div>
      )}

      {isMultiClass && (
        <div className="basis-full">
          <div
            className={clsx('-mx-3', {
              'bg-fv-blue-100 border-t-fv-blue-300 border-b border-t border-dashed border-b-gray-300':
                isQuoting,
            })}
          >
            {isQuoting && containsLength <= 2 && (
              <InlineContainsEditor {...props} />
            )}
            {(isBooking || containsLength > 2) && (
              <div className="flex flex-col">
                {value.contains?.map((c, ix) => (
                  <ContainsRow
                    key={`contains-${ix}`}
                    onEditClick={() => {
                      setEditingContains(value)
                      activateContains(ix)
                    }}
                    onChange={handleContainsChange(ix)}
                    value={c}
                    index={ix + 1}
                  />
                ))}
              </div>
            )}

            <InputGroupWrapper name="contains" className="my-4 px-4">
              <ManageContainsButton onClick={handleContainsOpen}>
                Add/edit commodities on this handling unit
              </ManageContainsButton>
            </InputGroupWrapper>
          </div>
        </div>
      )}
    </>
  )
}

const InlineContainsEditor = ({ value, onChange }: FormLayoutProps) => {
  const handleContainsChange =
    (index: number): ItemChangeFn =>
    v => {
      onChange({
        ...value,
        contains: value.contains?.map((c, ix) =>
          ix === index
            ? {
                ...c,
                ...v,
              }
            : c,
        ),
      })
    }
  return (
    <div>
      {value.contains?.slice(0, 2).map((item, index) => (
        <div
          className="border-fv-blue-400 odd:bg-fv-blue-50  border-b border-dashed px-4 pt-2"
          key={item?._id ?? index}
        >
          <ContainsForm
            value={item ?? { _id: '' }}
            onChange={handleContainsChange(index)}
          />
        </div>
      ))}
    </div>
  )
}

export const ManageContainsButton = ({
  onClick,
  children,
}: PropsWithChildren<Pick<FvButtonProps, 'onClick'>>) => (
  <FvButton
    className="flex items-center justify-start rounded-full"
    icon="pen-alt"
    onClick={onClick}
  >
    {children}
  </FvButton>
)
